import * as React from "react";
import {
	Box,
	Button,
	IconButton,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from "@mui/material";
import { PlusIcon } from "@phosphor-icons/react/dist/ssr/Plus";
import { TrashIcon } from "@phosphor-icons/react/dist/ssr/Trash";

import type { CampaignRequestCaptureField } from "@/hooks/api/use-campaign-requests";
import {
	useCreateCampaignRequestCaptureField,
	useDeleteCampaignRequestCaptureField,
	useUpdateCampaignRequestCaptureField,
} from "@/hooks/api/use-campaign-requests";

import { HelpTooltip } from "../components";

export interface CaptureFieldsEditorProps {
	requestId: string;
	fields: CampaignRequestCaptureField[];
	/** The request is settled: the fields stay legible, but nothing here can be written. */
	readOnly?: boolean;
}

/**
 * What to pull out of every conversation.
 *
 * A field is a title plus a description of what it is, and the description is what the extraction
 * actually runs on — "a number if given, otherwise leave empty" is doing real work — so the
 * placeholder teaches them to write it that way rather than as a note to a colleague.
 *
 * Rows are written to the API as they are edited rather than held until the step is finished,
 * because "Appears as" is generated by the API from the title and is stable across a later rename.
 * A client cannot be shown the variable they will use in the email until the row exists — and the
 * email is the next tab along, so a field added here has to be offered there without a save.
 *
 * Content only: the heading and the one-line explanation belong to the tab in `./step-outcomes`,
 * so all three jobs on that step are framed the same way.
 */
export function CaptureFieldsEditor({
	requestId,
	fields,
	readOnly = false,
}: CaptureFieldsEditorProps): React.JSX.Element {
	const createMutation = useCreateCampaignRequestCaptureField();
	const updateMutation = useUpdateCampaignRequestCaptureField();
	const deleteMutation = useDeleteCampaignRequestCaptureField();

	const [title, setTitle] = React.useState("");
	const [description, setDescription] = React.useState("");

	const handleAdd = async (): Promise<void> => {
		if (title.trim() === "") return;

		try {
			await createMutation.mutateAsync({
				requestId,
				data: { title: title.trim(), description: description.trim() },
			});
			setTitle("");
			setDescription("");
		} catch {
			// Reported by the hook.
		}
	};

	/** Saves on blur, and only when something actually changed. */
	const handleBlur = (field: CampaignRequestCaptureField, changes: Partial<CampaignRequestCaptureField>): void => {
		const nextTitle = changes.title ?? field.title;
		const nextDescription = changes.description ?? field.description;

		if (nextTitle === field.title && nextDescription === field.description) return;
		if (nextTitle.trim() === "") return;

		updateMutation.mutate({
			requestId,
			fieldId: field.id,
			data: { title: nextTitle.trim(), description: nextDescription },
		});
	};

	return (
		<Stack spacing={1.5}>
			<Box sx={{ overflowX: "auto" }}>
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell sx={{ fontWeight: 600, minWidth: 180 }}>Title</TableCell>
							<TableCell sx={{ fontWeight: 600, minWidth: 320 }}>What It Is</TableCell>
							<TableCell sx={{ fontWeight: 600, minWidth: 180 }}>Appears As</TableCell>
							<TableCell />
						</TableRow>
					</TableHead>
					<TableBody>
						{fields.map((field) => (
							<CaptureFieldRow
								key={field.id}
								field={field}
								readOnly={readOnly}
								busy={deleteMutation.isPending}
								onBlurSave={handleBlur}
								onDelete={() => deleteMutation.mutate({ requestId, fieldId: field.id })}
							/>
						))}

						{readOnly ? null : (
							<TableRow>
								<TableCell>
									<TextField
										fullWidth
										size="small"
										placeholder="Room count"
										value={title}
										onChange={(event) => setTitle(event.target.value)}
									/>
								</TableCell>
								<TableCell>
									<TextField
										fullWidth
										size="small"
										placeholder="Number of meeting rooms they plan to refresh. A number if given, otherwise leave empty."
										value={description}
										onChange={(event) => setDescription(event.target.value)}
									/>
								</TableCell>
								<TableCell colSpan={2}>
									<Button
										size="small"
										startIcon={<PlusIcon />}
										disabled={title.trim() === "" || createMutation.isPending}
										onClick={() => void handleAdd()}
									>
										Add a Field
									</Button>
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</Box>

			{/* One line. It used to name all three destinations and then give an example query,
			    which is a paragraph about our data model sitting under a two-column form. */}
			<Typography variant="caption" color="text.secondary">
				{/* "below" was true while this sat above the email in one long step. The email is a
				    tab of its own now, so it is named rather than pointed at. */}
				Each becomes a column in your export, a filter on your results, and a variable you can drop into the email.
			</Typography>
		</Stack>
	);
}

interface CaptureFieldRowProps {
	field: CampaignRequestCaptureField;
	readOnly: boolean;
	busy: boolean;
	onBlurSave: (field: CampaignRequestCaptureField, changes: Partial<CampaignRequestCaptureField>) => void;
	onDelete: () => void;
}

function CaptureFieldRow({ field, readOnly, busy, onBlurSave, onDelete }: CaptureFieldRowProps): React.JSX.Element {
	const [title, setTitle] = React.useState(field.title);
	const [description, setDescription] = React.useState(field.description);

	React.useEffect(() => {
		setTitle(field.title);
		setDescription(field.description);
	}, [field.title, field.description]);

	return (
		<TableRow>
			<TableCell>
				<TextField
					fullWidth
					size="small"
					disabled={readOnly}
					slotProps={{ htmlInput: { "aria-label": `Name of the field asking "${field.title}"` } }}
					value={title}
					onChange={(event) => setTitle(event.target.value)}
					onBlur={() => onBlurSave(field, { title })}
				/>
			</TableCell>
			<TableCell>
				<TextField
					fullWidth
					multiline
					size="small"
					disabled={readOnly}
					slotProps={{ htmlInput: { "aria-label": `What the agent should listen for in "${field.title}"` } }}
					value={description}
					onChange={(event) => setDescription(event.target.value)}
					onBlur={() => onBlurSave(field, { description })}
				/>
			</TableCell>
			<TableCell>
				{/* Generated by the API and stable across a rename of the title, on purpose. */}
				<Typography variant="body2" sx={{ fontFamily: "var(--mui-font-monospace, monospace)", wordBreak: "break-all" }}>
					{field.appearsAs}
				</Typography>
			</TableCell>
			<TableCell align="right">
				{readOnly ? null : (
					<HelpTooltip title="Remove">
						<span>
							<IconButton
								aria-label={`Remove ${field.title}`}
								color="error"
								size="small"
								disabled={busy}
								onClick={onDelete}
							>
								<TrashIcon />
							</IconButton>
						</span>
					</HelpTooltip>
				)}
			</TableCell>
		</TableRow>
	);
}
